import { useState, useEffect } from 'react';
import { Mail, MessageCircle, Phone, X, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { useTheme } from './Navbar';
import API_BASE from '../config';

/* ---- shared theme tokens (same system as Campaigns/Dashboard) ---- */
const TK = {
  light: {
    card:'#FFFFFF', border:'#ECEEF2', text:'#0F1623', sub:'#6B7280', muted:'#9CA3AF',
    accent:'#5B4EE8', accentBg:'#EEF0FD', accentTxt:'#4338CA',
    row:'#FAFBFC', input:'#FFFFFF', chip:'#F3F4F6', overlay:'rgba(15,22,35,0.45)',
    btnPri:'#5B4EE8', btnPriH:'#4C3FD4', trackOff:'#D1D5DB',
  },
  dark: {
    card:'#131320', border:'#1E1E2E', text:'#F1F2F6', sub:'#8B8FA8', muted:'#4B5263',
    accent:'#7C72FF', accentBg:'rgba(124,114,255,0.14)', accentTxt:'#A5A0FF',
    row:'#18182A', input:'#18182A', chip:'#1A1A2A', overlay:'rgba(0,0,0,0.7)',
    btnPri:'#6C63FF', btnPriH:'#5B53E8', trackOff:'#2A2A3A',
  },
};

const getToken = () =>
  localStorage.getItem('martech_token') || localStorage.getItem('token') ||
  sessionStorage.getItem('martech_token') || sessionStorage.getItem('token') || '';

const CHANNELS = [
  { id:'email',    label:'Email',    Icon:Mail,          desc:'Send personalised emails to your customers', accent:'#3B82F6' },
  { id:'whatsapp', label:'WhatsApp', Icon:MessageCircle, desc:'Reach customers via WhatsApp broadcast',      accent:'#10B981' },
  { id:'sms',      label:'SMS',      Icon:Phone,         desc:'Send SMS messages via MSG91',                 accent:'#8B5CF6' },
];

function InputField({ label, tk, ...rest }) {
  return (
    <div className="mb-4">
      <label style={{display:'block',color:tk.muted,fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:6}}>{label}</label>
      <input
        className="mtm-field w-full rounded-xl outline-none transition-all"
        style={{ '--ph':tk.muted, background:tk.input, border:`1px solid ${tk.border}`, color:tk.text, padding:'12px 16px', fontSize:14, boxSizing:'border-box' }}
        onFocus={e=>{ e.target.style.borderColor = tk.accent; }}
        onBlur={e=>{ e.target.style.borderColor = tk.border; }}
        {...rest}
      />
    </div>
  );
}

function TextareaField({ label, maxLen, tk, ...rest }) {
  const len = rest.value?.length || 0;
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1.5">
        <label style={{color:tk.muted,fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em'}}>{label}</label>
        {maxLen && <span style={{fontSize:11,color:len>maxLen*0.9?'#EF4444':tk.muted}}>{len}/{maxLen}</span>}
      </div>
      <textarea
        className="mtm-field w-full rounded-xl outline-none transition-all resize-none"
        style={{ '--ph':tk.muted, background:tk.input, border:`1px solid ${tk.border}`, color:tk.text, padding:'12px 16px', fontSize:14, minHeight:96, boxSizing:'border-box' }}
        onFocus={e=>{ e.target.style.borderColor = tk.accent; }}
        onBlur={e=>{ e.target.style.borderColor = tk.border; }}
        maxLength={maxLen}
        {...rest}
      />
    </div>
  );
}

function Toggle({ value, onChange, label, sub, tk }) {
  return (
    <div className="flex items-center justify-between mb-4 rounded-xl" style={{background:tk.input,border:`1px solid ${tk.border}`,padding:'12px 16px'}}>
      <div>
        <p style={{color:tk.text,fontSize:14,fontWeight:600,margin:0}}>{label}</p>
        {sub && <p style={{color:tk.muted,fontSize:12,margin:'2px 0 0'}}>{sub}</p>}
      </div>
      <button type="button" onClick={()=>onChange(!value)} className="relative rounded-full transition-colors duration-200"
        style={{width:44,height:24,background:value?tk.accent:tk.trackOff,border:'none',cursor:'pointer',flexShrink:0}}>
        <span className="absolute rounded-full transition-transform duration-200" style={{top:2,left:2,width:20,height:20,background:'#fff',transform:value?'translateX(20px)':'none'}}/>
      </button>
    </div>
  );
}

export default function CampaignModal({ onClose, onSuccess }) {
  const { theme } = useTheme();
  const tk = TK[theme];

  const [step,        setStep]        = useState(1);
  const [channel,     setChannel]     = useState(null);
  const [segments,    setSegments]    = useState([]);
  const [submitting,  setSubmitting]  = useState(false);

  const [segmentName,    setSegmentName]    = useState('');
  const [subject,        setSubject]        = useState('');
  const [body,           setBody]           = useState('');
  const [templateName,   setTemplateName]   = useState('');
  const [messageText,    setMessageText]    = useState('');
  const [scheduledAt,    setScheduledAt]    = useState('');
  const [dryRun,         setDryRun]         = useState(true);

  const tenantId = 'hospital_001';

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${API_BASE}/api/v1/segments`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.success) setSegments(d.segments || []); })
      .catch(() => {});
  }, []);

  const canProceed = segmentName && scheduledAt && (
    channel?.id === 'email'    ? (subject && body) :
    channel?.id === 'whatsapp' ? (templateName && messageText) :
    channel?.id === 'sms'      ? !!messageText : false
  );

  const handleSubmit = async () => {
    const token = getToken();
    setSubmitting(true);
    try {
      const payload = {
        segment_name:  segmentName,
        channel:       channel.id,
        template_name: channel.id === 'email' ? (subject || 'email_campaign') : templateName || 'default',
        tenant_id:     tenantId,
        scheduled_at:  scheduledAt,
        dry_run:       dryRun,
        message_body:  channel.id === 'email' ? body : messageText,
      };
      const res  = await fetch(`${API_BASE}/api/v1/campaign/schedule`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      onSuccess?.(data.success || res.ok ? 'Campaign scheduled successfully' : 'Campaign queued', data.success || res.ok ? 'green' : 'blue');
      onClose();
    } catch {
      onSuccess?.('Campaign queued for processing', 'blue');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const fieldLabel = { display:'block', color:tk.muted, fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 };

  const CommonFields = (
    <>
      <div className="mb-4">
        <label style={fieldLabel}>Segment</label>
        <select
          value={segmentName}
          onChange={e => setSegmentName(e.target.value)}
          className="w-full rounded-xl outline-none"
          style={{ background:tk.input, border:`1px solid ${tk.border}`, color:tk.text, padding:'12px 16px', fontSize:14, appearance:'none', colorScheme:theme, boxSizing:'border-box' }}
        >
          <option value="">Select segment…</option>
          {segments.map(s => (
            <option key={s.segment_name} value={s.segment_name}>
              {s.segment_name.replace(/_/g, ' ')} ({s.count})
            </option>
          ))}
        </select>
      </div>
      <div className="mb-4">
        <label style={fieldLabel}>Scheduled At</label>
        <input
          type="datetime-local"
          value={scheduledAt}
          onChange={e => setScheduledAt(e.target.value)}
          className="w-full rounded-xl outline-none"
          style={{ background:tk.input, border:`1px solid ${tk.border}`, color:tk.text, padding:'12px 16px', fontSize:14, colorScheme:theme, boxSizing:'border-box' }}
          onFocus={e => { e.target.style.borderColor = tk.accent; }}
          onBlur={e  => { e.target.style.borderColor = tk.border; }}
        />
      </div>
      <Toggle value={dryRun} onChange={setDryRun} label="Dry Run" sub="Test without sending real messages" tk={tk} />
    </>
  );

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <style>{`.mtm-field::placeholder{color:var(--ph);opacity:1}`}</style>

      <div className="absolute inset-0 backdrop-blur-sm" style={{background:tk.overlay}} onClick={onClose} />

      <div className="relative z-10 w-full max-w-lg rounded-2xl animate-slideUp max-h-[90vh] overflow-y-auto"
        style={{ background:tk.card, border:`1px solid ${tk.border}` }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5" style={{ borderBottom:`1px solid ${tk.border}` }}>
          <div>
            <h2 style={{fontSize:18,fontWeight:800,color:tk.text,margin:0}}>New Campaign</h2>
            <div className="flex items-center gap-2 mt-1.5">
              {[1, 2, 3].map(s => {
                const done = step > s, cur = step === s;
                return (
                  <div key={s} className="flex items-center gap-1.5">
                    <div className="flex items-center justify-center"
                      style={{ width:20, height:20, borderRadius:'50%', fontSize:10, fontWeight:800,
                        background: done?tk.accent : cur?tk.accentBg : tk.chip,
                        color: done?'#fff' : cur?tk.accentTxt : tk.muted,
                        border: cur?`1px solid ${tk.accent}66`:'1px solid transparent' }}>
                      {done ? <Check className="w-3 h-3" /> : s}
                    </div>
                    {s < 3 && <div style={{width:32,height:1,background:done?tk.accent:tk.border}} />}
                  </div>
                );
              })}
              <span style={{color:tk.sub,fontSize:12,marginLeft:4}}>
                {step === 1 ? 'Channel' : step === 2 ? 'Configure' : 'Review'}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="flex items-center justify-center rounded-xl transition-all"
            style={{width:32,height:32,background:'transparent',border:`1px solid ${tk.border}`,color:tk.sub,cursor:'pointer'}}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">

          {/* ── Step 1: Channel ── */}
          {step === 1 && (
            <div className="space-y-3">
              <p style={{color:tk.sub,fontSize:14,marginBottom:16}}>Select your campaign channel</p>
              {CHANNELS.map(ch => (
                <button key={ch.id}
                  onClick={() => { setChannel(ch); setStep(2); }}
                  className="w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all duration-200"
                  style={{ background:tk.card, border:`1px solid ${tk.border}` }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor = tk.accent; e.currentTarget.style.background = tk.row; }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor = tk.border; e.currentTarget.style.background = tk.card; }}>
                  <div className="flex items-center justify-center flex-shrink-0"
                    style={{ width:48, height:48, borderRadius:12, background:ch.accent+'18', border:`1px solid ${ch.accent}33` }}>
                    <ch.Icon className="w-6 h-6" style={{color:ch.accent}} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p style={{color:tk.text,fontWeight:700,fontSize:14,margin:0}}>{ch.label}</p>
                    <p style={{color:tk.sub,fontSize:12,margin:'2px 0 0'}}>{ch.desc}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 flex-shrink-0" style={{color:tk.muted}} />
                </button>
              ))}
            </div>
          )}

          {/* ── Step 2: Configure ── */}
          {step === 2 && channel && (
            <div>
              <div className="flex items-center gap-2 mb-5">
                <div className="flex items-center justify-center" style={{width:24,height:24,borderRadius:8,background:channel.accent+'18'}}>
                  <channel.Icon className="w-3.5 h-3.5" style={{color:channel.accent}} />
                </div>
                <p style={{color:tk.text,fontWeight:700,fontSize:14,margin:0}}>{channel.label} Campaign</p>
              </div>

              {channel.id === 'email' && (
                <>
                  {CommonFields}
                  <InputField label="Subject Line" tk={tk} value={subject} onChange={e => setSubject(e.target.value)} placeholder="Your email subject…" />
                  <TextareaField label="Email Body" tk={tk} value={body} onChange={e => setBody(e.target.value)} placeholder="Write your email content…" />
                </>
              )}

              {channel.id === 'whatsapp' && (
                <>
                  {CommonFields}
                  <InputField label="Template Name" tk={tk} value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="pre_approved_template_name" />
                  <p style={{color:'#D97706',fontSize:11,marginTop:-12,marginBottom:16}}>Must be pre-approved by Meta</p>
                  <TextareaField label="Message Body" tk={tk} value={messageText} onChange={e => setMessageText(e.target.value)} placeholder="Write your WhatsApp message…" />
                </>
              )}

              {channel.id === 'sms' && (
                <>
                  {CommonFields}
                  <TextareaField label="Message Text" tk={tk} value={messageText} onChange={e => setMessageText(e.target.value)} placeholder="Write your SMS message (160 chars)…" maxLen={160} />
                </>
              )}

              <div className="flex items-center justify-between mt-2 pt-4" style={{ borderTop:`1px solid ${tk.border}` }}>
                <button onClick={() => setStep(1)} className="flex items-center gap-2 text-sm font-semibold transition-colors"
                  style={{color:tk.sub,background:'transparent',border:'none',cursor:'pointer'}}>
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={() => setStep(3)} disabled={!canProceed}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all"
                  style={{ background:tk.btnPri, color:'#fff', border:'none', cursor:canProceed?'pointer':'not-allowed', opacity:canProceed?1:0.4 }}
                  onMouseEnter={e=>{ if(canProceed) e.currentTarget.style.background=tk.btnPriH; }}
                  onMouseLeave={e=>{ e.currentTarget.style.background=tk.btnPri; }}>
                  Review <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Review ── */}
          {step === 3 && channel && (
            <div>
              <p style={{color:tk.sub,fontSize:14,marginBottom:20}}>Review before scheduling</p>
              <div className="rounded-xl p-4 space-y-3 mb-5" style={{ background:tk.row, border:`1px solid ${tk.border}` }}>
                {[
                  { label: 'Channel',   value: channel.label },
                  { label: 'Segment',   value: segmentName.replace(/_/g, ' ') || '—' },
                  { label: 'Tenant',    value: tenantId },
                  { label: 'Scheduled', value: scheduledAt ? new Date(scheduledAt).toLocaleString() : '—' },
                  { label: 'Dry Run',   value: dryRun ? 'Yes (safe mode)' : 'No (live send)' },
                  channel.id === 'email' && { label: 'Subject',  value: subject || '—' },
                  channel.id !== 'email' && { label: 'Template', value: templateName || '—' },
                  channel.id !== 'email' && messageText && { label: 'Message', value: messageText.length > 60 ? messageText.slice(0, 60) + '…' : messageText },
                ].filter(Boolean).map(({ label, value }) => (
                  <div key={label} className="flex items-start justify-between gap-4">
                    <span style={{color:tk.muted,fontSize:11,textTransform:'uppercase',letterSpacing:'0.05em',fontWeight:600,flexShrink:0}}>{label}</span>
                    <span style={{color:tk.text,fontSize:14,textAlign:'right',fontWeight:500,wordBreak:'break-word'}}>{value}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <button onClick={() => setStep(2)} className="flex items-center gap-2 text-sm font-semibold transition-colors"
                  style={{color:tk.sub,background:'transparent',border:'none',cursor:'pointer'}}>
                  <ChevronLeft className="w-4 h-4" /> Edit
                </button>
                <button onClick={handleSubmit} disabled={submitting}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all"
                  style={{ background:tk.btnPri, color:'#fff', border:'none', cursor:'pointer', opacity:submitting?0.5:1 }}
                  onMouseEnter={e=>{ if(!submitting) e.currentTarget.style.background=tk.btnPriH; }}
                  onMouseLeave={e=>{ e.currentTarget.style.background=tk.btnPri; }}>
                  {submitting ? (
                    <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Scheduling…</>
                  ) : (
                    <><Check className="w-4 h-4" /> Schedule Campaign</>
                  )}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}